import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/features/auth/components";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import { Toaster } from "@/components/ui/toaster";

import { LoginPage, DashboardPage, UnauthorizedPage, NotFoundPage } from "@/pages";
import {
  UsersPage,
  LocationsPage,
  LLMProvidersPage,
  LoanRulesPage,
  ElectreSettingsPage,
  DigitalResourcesSettingsPage,
  MailSettingsPage,
  ImportFondsPage,
} from "@/features/admin/pages";
import {
  CatalogSearchPage,
  RecordDetailPage,
  RecordEditPage,
  RecordImportPage,
  WorksListPage,
  WorkDetailPage,
} from "@/features/catalog/pages";
import {
  CirculationDeskPage,
  PatronsPage,
  PatronCreatePage,
  PatronDetailPage,
  ReservationsPage,
} from "@/features/circulation/pages";
import { SuppliersPage, OrdersPage, BudgetsPage } from "@/features/acquisition/pages";
import { SubscriptionsPage, SerialControlPage } from "@/features/periodical/pages";
import { AISuggestionsPage, CopilotPage, WeedingPage } from "@/features/ai/pages";
import {
  ExternalSearchPage,
  MigrationPage,
  ElectreBasketsPage,
} from "@/features/interoperability/pages";
import { ImportCampaignDetailPage } from "@/features/import";
import {
  StatisticsOverviewPage,
  CirculationStatsPage,
  CollectionStatsPage,
} from "@/features/statistics/pages";

// Public portal imports
import { PatronAuthProvider } from "@/features/portal/contexts/PatronAuthContext";
import { PublicLayout } from "@/features/portal/components";
import {
  HomePage as PortalHomePage,
  SearchPage as PortalSearchPage,
  RecordDetailPage as PortalRecordDetailPage,
  PatronLoginPage,
  AccountPage,
  LegalNoticePage,
  PrivacyPolicyPage,
  DigitalResourcesPage,
  AccountAccessRequestPage,
  AccountAccessPasswordPage,
} from "@/features/portal/pages";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* ========================================= */}
          {/* PUBLIC PORTAL (OPAC) - No staff auth     */}
          {/* ========================================= */}
          <Route
            path="/opac/*"
            element={
              <PatronAuthProvider>
                <Routes>
                  <Route element={<PublicLayout />}>
                    <Route index element={<PortalHomePage />} />
                    <Route path="search" element={<PortalSearchPage />} />
                    <Route path="record/:id" element={<PortalRecordDetailPage />} />
                    <Route path="new-arrivals" element={<PortalSearchPage />} />
                    <Route path="ressources-numeriques" element={<DigitalResourcesPage />} />
                    <Route path="login" element={<PatronLoginPage />} />
                    <Route path="acces-compte" element={<AccountAccessRequestPage />} />
                    <Route
                      path="acces-compte/mot-de-passe"
                      element={<AccountAccessPasswordPage />}
                    />
                    <Route path="account" element={<AccountPage />} />
                    <Route path="account/*" element={<AccountPage />} />
                    <Route path="mentions-legales" element={<LegalNoticePage />} />
                    <Route path="confidentialite" element={<PrivacyPolicyPage />} />
                  </Route>
                </Routes>
              </PatronAuthProvider>
            }
          />

          {/* ========================================= */}
          {/* PROFESSIONAL INTERFACE - Staff auth      */}
          {/* ========================================= */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <Routes>
                  {/* Public routes (no layout) */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />

                  {/* Protected routes with MainLayout */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <ProtectedLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Dashboard */}
                    <Route index element={<DashboardPage />} />

                    {/* Catalog routes */}
                    <Route path="catalog" element={<CatalogSearchPage />} />
                    <Route
                      path="catalog/import"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <RecordImportPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="catalog/new"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <RecordEditPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="catalog/:id" element={<RecordDetailPage />} />
                    <Route
                      path="catalog/:id/edit"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <RecordEditPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Works routes (IFLA LRM) */}
                    <Route path="catalog/works" element={<WorksListPage />} />
                    <Route path="catalog/works/:id" element={<WorkDetailPage />} />

                    {/* Document Import routes (CSV campaigns) - Admin only */}
                    <Route
                      path="import"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <ImportFondsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="import/:id"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <ImportCampaignDetailPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Circulation routes */}
                    <Route
                      path="circulation"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CIRCULATION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <CirculationDeskPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="circulation/reservations"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CIRCULATION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <ReservationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="circulation/patrons"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CIRCULATION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <PatronsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="circulation/patrons/new"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CIRCULATION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <PatronCreatePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="circulation/patrons/:id"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CIRCULATION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <PatronDetailPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Acquisition routes */}
                    <Route
                      path="acquisition/suppliers"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_ACQUISITION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <SuppliersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="acquisition/orders"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_ACQUISITION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <OrdersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="acquisition/budgets"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_ACQUISITION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <BudgetsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Periodical routes */}
                    <Route
                      path="periodical/subscriptions"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_ACQUISITION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <SubscriptionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="periodical/serial-control"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_ACQUISITION", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <SerialControlPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* AI routes */}
                    <Route path="ai/copilot" element={<CopilotPage />} />
                    <Route
                      path="ai/suggestions"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <AISuggestionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="ai/weeding"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <WeedingPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Statistics routes */}
                    <Route path="statistics" element={<StatisticsOverviewPage />} />
                    <Route path="statistics/circulation" element={<CirculationStatsPage />} />
                    <Route path="statistics/collections" element={<CollectionStatsPage />} />

                    {/* Interoperability routes */}
                    <Route
                      path="interop/search"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <ExternalSearchPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="interop/electre-baskets"
                      element={
                        <ProtectedRoute
                          requiredRoles={["ROLE_CATALOGER", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
                        >
                          <ElectreBasketsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="interop/migration"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <MigrationPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin routes */}
                    <Route
                      path="admin/users"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <UsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/locations"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <LocationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/loan-rules"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <LoanRulesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/mail"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <MailSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/digital-resources"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <DigitalResourcesSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/electre"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <ElectreSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/ai-providers"
                      element={
                        <ProtectedRoute requiredRoles={["ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}>
                          <LLMProvidersPage />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <Toaster />
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
